import { Alert, Button, PasswordInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState, type CSSProperties } from "react";
import { Navigate, useLocation } from "react-router";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { semanticColor } from "@how/design-system";
import { linkError, useSession, useSupabase } from "../session";
import { useEmailLinkTarget } from "../tenant";
import { BrandPanel, DEFAULT_QUOTE, type SignInQuote } from "./BrandPanel";
import { primaryVars, secondaryVars } from "./buttons";
import "./sign-in.css";

const schema = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});
type Values = z.infer<typeof schema>;

/**
 * Whether the page was opened through an old address's forward (`moved=1`, spec section 9). Read once at load, like
 * linkError: the forward lands on a church page, and the session guard then sends the visitor to sign-in without the
 * query.
 */
export function cameFromOldAddress(search: string): boolean {
  return new URLSearchParams(search).get("moved") === "1";
}
const movedAtLoad = typeof window !== "undefined" && cameFromOldAddress(window.location.search);

export interface SignInPageProps {
  /** Surface name shown beside the wordmark, e.g. "HoW Control Plane". */
  readonly title: string;
  /** One line under the headline, e.g. "Church staff sign-in". */
  readonly subtitle: string;
  /** Fine print under the form; who can have an account here. */
  readonly footnote?: string | undefined;
  /** Pull quote on the brand panel (large screens). */
  readonly quote?: SignInQuote | undefined;
  /** Shown when the visitor arrived from an old address (`moved=1`): why they sign in again. */
  readonly movedNotice?: string | undefined;
}

const fieldClasses = {
  root: "how-signin-field",
  label: "how-signin-label",
  required: "how-signin-required",
  input: "how-signin-input",
  error: "how-signin-error",
};
const passwordClasses = {
  ...fieldClasses,
  innerInput: "how-signin-inner-input",
  visibilityToggle: "how-signin-toggle",
};
/**
 * Split-screen sign-in shared by every HoW surface: animated brand panel on the left, credentials on the right, in
 * the landing page's Vetra-derived palette. Password sign-in plus a magic-link alternative; MFA follows in RequireMfa.
 */
export function SignInPage({
  title,
  subtitle,
  footnote = "Access is by invitation. Ask your administrator if you need an account.",
  quote = DEFAULT_QUOTE,
  movedNotice,
}: SignInPageProps) {
  const supabase = useSupabase();
  const linkTarget = useEmailLinkTarget();
  const session = useSession();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const form = useForm<Values>({
    initialValues: { email: "", password: "" },
    validate: zod4Resolver(schema),
  });
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const moved = movedNotice !== undefined && (movedAtLoad || cameFromOldAddress(location.search));
  if (session.status === "signed_in") return <Navigate to={from} replace />;

  const signIn = async (values: Values): Promise<void> => {
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword(values);
    setSubmitting(false);
    if (error) {
      notifications.show({
        color: semanticColor("danger"),
        title: "Sign-in failed",
        message: "Check your email and password and try again.",
      });
      return;
    }
    notifications.show({ color: "teal", title: "Signed in", message: "Welcome back." });
  };

  const magicLink = async (): Promise<void> => {
    const email = form.values.email;
    if (!schema.shape.email.safeParse(email).success) {
      form.setFieldError("email", "Enter a valid email address");
      return;
    }
    // On a church's own address the link opens at HoW's address for the church (R8); until that is known, nothing is
    // sent rather than a link Supabase would send to its site URL instead.
    if (linkTarget === null) {
      notifications.show({
        color: semanticColor("danger"),
        title: "Could not send a link",
        message: "Try again in a moment.",
      });
      return;
    }
    setSending(true);
    // Accounts are created by invitation, never by a magic link.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: linkTarget.redirectTo,
        shouldCreateUser: false,
      },
    });
    setSending(false);
    notifications.show(
      error
        ? {
            color: semanticColor("danger"),
            title: "Could not send a link",
            message:
              error.code === "signup_disabled" || error.code === "otp_disabled"
                ? "No confirmed account uses this email. Ask your administrator for an invitation."
                : error.code === "over_email_send_rate_limit"
                  ? "Too many emails were sent recently. Wait a few minutes and try again."
                  : "Try again in a moment.",
          }
        : { color: "teal", title: "Check your email", message: "We sent you a sign-in link." },
    );
  };

  return (
    <main className="how-signin">
      <BrandPanel title={title} quote={quote} />
      <div className="how-signin-form-panel">
        <div
          className="how-signin-form how-signin-reveal"
          style={{ "--delay": "0.1s" } as CSSProperties}
        >
          <h1 className="how-signin-heading">Welcome back.</h1>
          <p className="how-signin-sub">{subtitle}</p>
          {moved ? (
            <Alert
              color={semanticColor("info")}
              variant="light"
              mt="md"
              radius="md"
              role="status"
              title="One address for all your churches"
            >
              {movedNotice}
            </Alert>
          ) : null}
          {linkError === null ? null : (
            <Alert color="red" variant="light" mt="md" radius="md" title="Sign-in link">
              {linkError}
            </Alert>
          )}
          <form
            className="how-signin-fields"
            onSubmit={form.onSubmit((values) => {
              void signIn(values);
            })}
          >
            <TextInput
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@church.org"
              required
              classNames={fieldClasses}
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label="Password"
              autoComplete="current-password"
              placeholder="••••••••"
              required
              classNames={passwordClasses}
              {...form.getInputProps("password")}
            />
            <Button
              type="submit"
              loading={submitting}
              fullWidth
              className="how-signin-primary"
              vars={primaryVars}
            >
              Sign in
            </Button>
          </form>
          <div className="how-signin-divider" aria-hidden="true">
            <span />
            <p>or</p>
            <span />
          </div>
          <Button
            type="button"
            fullWidth
            loading={sending}
            className="how-signin-secondary"
            vars={secondaryVars}
            onClick={() => {
              void magicLink();
            }}
          >
            Email me a sign-in link
          </Button>
          {linkTarget === null || linkTarget.opensAt === null ? null : (
            <p className="how-signin-fine">The link opens at {linkTarget.opensAt}.</p>
          )}
          <p className="how-signin-fine">{footnote}</p>
        </div>
      </div>
    </main>
  );
}
